import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import MascotBubble from "./MascotBubble";
import { PeopleStep, PetsStep, newMember } from "./household/PeopleSteps";
import { AllergiesStep, TastesStep, CookingStep, PlanningStep, SupermarketStep, KitchenStep } from "./household/Steps";

const STEPS = [
  { key: "name", caption: "G'day! First up — what should I call your household?" },
  { key: "people", caption: "Who am I cooking for? Adults, kids and their ages." },
  { key: "pets", caption: "Any pets in the house? I'll add their tucker to the shop." },
  { key: "allergies", caption: "Any allergies, intolerances or dietary needs?" },
  { key: "tastes", caption: "What does everyone love — and what's a hard no?" },
  { key: "cooking", caption: "How handy are you in the kitchen, and what gear have you got?" },
  { key: "planning", caption: "How many dinners, what's the budget, and which nights are hectic?" },
  { key: "shops", caption: "Where do you do the groceries? I'll find your local shops." },
  { key: "kitchen", caption: "Last one! What's already in the kitchen, plus any household bits?" },
];

const DEFAULTS = {
  name: "", members: [newMember("adult")], pets: [], allergies: [], dietary_requirements: [],
  likes: [], dislikes: [], favourite_meals: [], never_suggest: [], cooking_skill: "confident", appliances: ["Oven", "Stovetop", "Microwave"],
  meals_per_week: 7, include_lunches: false, include_breakfasts: false, budget_weekly: 250, busy_nights: [],
  postcode: "", suburb: "", supermarkets: [], inventory: [], household_items: [], cuisine_prefs: [], taste_style: "middle",
};

export default function HouseholdForm({ initial, onSubmit, submitLabel = "Sort my week", loading }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    const merged = { ...DEFAULTS, ...(initial || {}) };
    merged.members = merged.members?.length ? merged.members.map((m) => ({ ...m, role: m.role || (m.age_group === "adult" ? "adult" : "child") })) : [newMember("adult")];
    return merged;
  });
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const canNext = () => {
    if (step === 0) return form.name.trim().length > 0;
    if (step === 1) return form.members.some((m) => m.name.trim());
    return true;
  };

  const submit = () => {
    onSubmit({
      ...form,
      name: form.name.trim(),
      members: form.members.filter((m) => m.name.trim()).map((m) => ({ ...m, name: m.name.trim(), fussy_level: Number(m.fussy_level), age: m.role === "child" ? m.age : null })),
      pets: form.pets.filter((p) => p.name.trim() || p.food.trim()),
      budget_weekly: Number(form.budget_weekly),
      currency: "A$",
    });
  };

  const last = step === STEPS.length - 1;

  return (
    <div className="px-6 pt-10 pb-8">
      <div className="flex gap-1.5 mb-6" data-testid="onboarding-progress">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-sorted-peach" : "bg-[#FFE7E1]"}`} />
        ))}
      </div>

      <MascotBubble caption={STEPS[step].caption} size={52} mood={["happy","idea","love","thinking","happy","cooking","thinking","point","celebrate"][step]} />

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="mt-6" data-testid={`step-${STEPS[step].key}`}>
          {step === 0 && (
            <div>
              <label className="text-sm font-bold text-sorted-ink">Household name</label>
              <input data-testid="household-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="The Hulcombe Kitchen"
                className="mt-2 w-full bg-white border-2 border-[#FFF3F0] rounded-2xl px-4 py-4 font-medium text-sorted-ink outline-none focus:border-sorted-peach transition-colors" />
            </div>
          )}
          {step === 1 && <PeopleStep members={form.members} setMembers={(v) => set("members", v)} />}
          {step === 2 && <PetsStep pets={form.pets} setPets={(v) => set("pets", v)} />}
          {step === 3 && <AllergiesStep form={form} set={set} />}
          {step === 4 && <TastesStep form={form} set={set} />}
          {step === 5 && <CookingStep form={form} set={set} />}
          {step === 6 && <PlanningStep form={form} set={set} />}
          {step === 7 && <SupermarketStep form={form} set={set} />}
          {step === 8 && <KitchenStep form={form} set={set} onSkip={submit} skipping={loading} />}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center gap-3 mt-8">
        {step > 0 && (
          <button type="button" onClick={() => setStep(step - 1)} data-testid="onboarding-back"
            className="w-14 h-14 rounded-full bg-white border-2 border-[#FFF3F0] text-sorted-ink flex items-center justify-center shrink-0">
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
        )}
        {!last ? (
          <button type="button" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="onboarding-next"
            className="flex-1 bg-sorted-peach hover:bg-[#FCA696] disabled:opacity-50 text-sorted-ink font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft transition-colors">
            Continue <ArrowRight size={18} strokeWidth={2.5} />
          </button>
        ) : (
          <button type="button" disabled={loading} onClick={submit} data-testid="onboarding-submit"
            className="flex-1 bg-sorted-peach hover:bg-[#FCA696] disabled:opacity-60 text-sorted-ink font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft transition-colors">
            {loading ? "Sorting…" : submitLabel} {!loading && <Check size={18} strokeWidth={2.5} />}
          </button>
        )}
      </div>
    </div>
  );
}
